<template>
  <div>
    <Modal
      v-model:isOpen="isAddContainerModalOpen"
      modalId="tracking-add-container-modal"
      maxWidth="6xl"
      ariaLabel="Add containers"
    >
      <template #title>
        Scrape &amp; Add New Container(s)
      </template>
      <template #subtitle>
        Scrape containers by container number (from Terminal website) or by Bill of Lading number (from Steamship or Terminal website).
      </template>

      <template #content>

          <div class="tw-px-4 tw-pt-4 tw-pb-3 sm:tw-px-5">
            <div class="tw-flex tw-flex-wrap tw-gap-2">
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium"
                :class="activeTab === 'container' ? 'tw-border-slate-900 tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200/70 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="activeTab = 'container'"
              >
                by Container Number
              </button>
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium"
                :class="activeTab === 'bl' ? 'tw-border-slate-900 tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200/70 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50'"
                @click="activeTab = 'bl'"
              >
                by Bill Of Lading Number
              </button>
            </div>

            <div v-if="fetchedContainers" class="tw-mt-4 tw-rounded-2xl tw-border tw-border-emerald-200/70 tw-bg-emerald-50/60 tw-p-4">
              <h2
                v-if="fetchedBL"
                class="tw-text-sm sm:tw-text-base tw-font-semibold tw-text-emerald-900"
              >Found {{fetchedContainers.length}} containers for BL {{fetchedBL.billOfLadingNumber}}</h2>
              <h2 v-else class="tw-text-sm sm:tw-text-base tw-font-semibold tw-text-emerald-900">Found {{fetchedContainers.length}} containers!</h2>
              <div class="tw-mt-3 tw-overflow-x-auto">
                <table class="tw-w-full tw-text-left tw-text-sm">
                  <thead class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-emerald-900/70">
                    <tr>
                      <th class="tw-px-3 tw-py-2">Container Number</th>
                      <th class="tw-px-3 tw-py-2">Size/Type</th>
                      <th class="tw-px-3 tw-py-2">Yard Status</th>
                      <th class="tw-px-3 tw-py-2">Terminal</th>
                    </tr>
                  </thead>
                  <tbody class="tw-divide-y tw-divide-emerald-200/60">
                    <tr
                      v-for="(container, containerIdx)  in fetchedContainers"
                      v-bind:key="`fetched-container-${containerIdx}`"
                      class="tw-text-emerald-950"
                    >
                      <td class="tw-px-3 tw-py-2 tw-font-medium">{{container.containerId}}</td>
                      <td class="tw-px-3 tw-py-2">{{container.sizeType}}</td>
                      <td class="tw-px-3 tw-py-2">{{container.yardStatusDescription}}</td>
                      <td class="tw-px-3 tw-py-2">{{container.terminal}}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="tw-mt-4 tw-flex tw-justify-end">
                <button class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800" @click="resetContainers">OK</button>
              </div>
            </div>

            <div class="tw-mt-4">
              <div v-if="activeTab === 'container'">
                <div v-if="!fetchedContainers">
                  <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                    <div>
                      <div class="tw-text-sm tw-font-medium tw-text-slate-700">Container numbers</div>
                      <div class="tw-mt-1 tw-text-xs tw-text-slate-500">Enter one container per line.</div>
                      <textarea
                        v-model="containerNumbers"
                        cols="30"
                        rows="10"
                        class="tw-mt-3 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2.5 tw-text-sm tw-leading-6 tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/20"
                        :class="containerFormError ? 'tw-border-rose-300 focus:tw-ring-rose-500/20' : ''"
                        :placeholder="'MSCU1234567\nTGHU7654321'"
                      ></textarea>
                      <div v-if="containerFormError && hasAttemptedContainerSubmit" class="tw-mt-2 tw-text-sm tw-text-rose-600">
                        {{ containerFormError }}
                      </div>
                    </div>

                    <div>
                      <div class="tw-text-sm tw-font-medium tw-text-slate-700">Terminal</div>
                      <div class="tw-mt-1 tw-text-xs tw-text-slate-500">&nbsp;</div>
                      <div class="tw-mt-3 tw-grid tw-grid-cols-1 tw-gap-2">
                        <label v-for="terminalId in ['TRAPAC', 'OICT', 'ETS']" class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50 tw-px-3 tw-py-2">
                          <input
                            class="tw-cursor-pointer"
                            type="radio"
                            name="terminalForContainerScraping"
                            :value="terminalId"
                            v-model="terminal"
                          />
                          <span class="tw-text-sm tw-font-medium tw-text-slate-800">{{ terminalId }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="tw-mt-4">
                  <p
                    v-if="hasAttemptedContainerSubmit && !isContainerListValid"
                    class="tw-text-sm tw-text-rose-600"
                  >Your container Numbers are not valid. Please verify.</p>
                  <div class="tw-mt-2 tw-flex">
                    <button
                      type="button"
                      class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800"
                      :class="!canSubmitContainers ? 'tw-opacity-60' : ''"
                      :disabled="loadingContainer || !canSubmitContainers"
                      v-on:click="trackContainer"
                    >Scrape &amp; Add Container(s)</button>
                  </div>
                </div>

              </div>

              <div v-else>
                <div v-if="!fetchedBL">
                  <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                    <div>
                      <label for="blNumber" class="tw-text-sm tw-font-medium tw-text-slate-700">Bill Of Lading Number</label>
                      <div class="tw-mt-1 tw-text-xs tw-text-slate-500">Enter BL number to scrape containers for (only ONE BL number allowed at a time)</div>
                      <input
                        v-model="billOfLadingNumber"
                        type="text"
                        id="blNumber"
                        class="tw-mt-2 tw-w-full tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/20"
                        :class="textDanger ? 'tw-border-rose-300 focus:tw-ring-rose-500/20' : ''"
                        placeholder="e.g. ONEY123456789"
                      />
                      <div >
                        <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-xl tw-bg-white/50 tw-px-3 tw-py-2">
                          <input
                            class="tw-cursor-pointer"
                            type="radio"
                            name="typeOfBlScraping"
                            value="shiplineWebsite"
                            v-model="billOfLadingNumberScrapeSource"
                          />
                          <span class="tw-text-sm tw-font-medium tw-text-slate-800">from Steamshipline Website </span>
                        </label>
                        <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-xl tw-px-3 tw-py-2">
                          <input
                            class="tw-cursor-pointer"
                            type="radio"
                            name="typeOfBlScraping"
                            value="terminalWebsite"
                            v-model="billOfLadingNumberScrapeSource"
                          />
                          <span class="tw-text-sm tw-font-medium tw-text-slate-800">from Terminal Website </span>
                        </label>
                        
                      </div>

                    </div>

                    <div v-if="billOfLadingNumberScrapeSource === 'terminalWebsite'">
                      <div class="tw-text-sm tw-font-medium tw-text-slate-700">Terminal</div>
                      <div class="tw-mt-1 tw-text-xs tw-text-slate-500">&nbsp;</div>
                      <div class="tw-mt-2 tw-grid tw-grid-cols-1 tw-gap-2">
                        <label v-for="terminalId in ['OICT', 'ETS', 'TRAPAC']" class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50 tw-px-3 tw-py-2">
                          <input
                            class="tw-cursor-pointer"
                            type="radio"
                            name="terminalForBlScraping"
                            :value="terminalId"
                            v-model="blScrapeTerminal"
                          />
                          <span class="tw-text-sm tw-font-medium tw-text-slate-800">{{ terminalId }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div v-if="blFormError">
                    <p class="tw-mt-3 tw-text-sm tw-text-rose-600">{{ blFormError }}</p>
                    <button
                      class="tw-mt-2 tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800"
                      v-if="billOfLadingNumber && !blManualOverride && (askManualOverride)"
                      @click="triggerManualBLEntry"
                    >Manually Add Bill of Lading</button>
                    <div v-else-if="billOfLadingNumber && blManualOverride" class="tw-mt-4 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-p-4">
                      <p class="tw-text-sm tw-font-medium tw-text-slate-900">Enter all the containers for {{billOfLadingNumber}}</p>
                      <p class="tw-mt-1 tw-text-xs tw-text-slate-500">Enter one container per line.</p>
                      <textarea
                        v-model="manualContainers"
                        cols="30"
                        rows="10"
                        class="tw-mt-3 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2.5 tw-text-sm tw-leading-6 tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/20"
                        :placeholder="'MSCU1234567\nTGHU7654321'"
                      ></textarea>

                      <div class="tw-mt-3 tw-text-sm tw-font-medium tw-text-slate-700">Terminal</div>
                      <div class="tw-mt-2 tw-grid tw-grid-cols-1 tw-gap-2">
                        <label v-for="terminalId in ['TRAPAC', 'ETS', 'OICT']" class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50 tw-px-3 tw-py-2">
                          <input
                            class="tw-cursor-pointer"
                            type="radio"
                            name="terminalForManualContainerEntry"
                            :value="terminalId"
                            v-model="manualContainersTerminal"
                          />
                          <span class="tw-text-sm tw-font-medium tw-text-slate-800">{{ terminalId }}</span>
                        </label>
                      </div>

                      <p
                        v-if="hasAttemptedManualSubmit && !isManualContainerListValid"
                        class="tw-mt-3 tw-text-sm tw-text-rose-600"
                      >Your container Numbers are not valid. Please verify.</p>

                      <div class="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
                        <button
                          class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800"
                          @click="addBLWithContainersManually"
                          :class="!canSubmitManualContainers ? 'tw-opacity-60' : ''"
                          :disabled="loadingManualBL || !canSubmitManualContainers"
                        >Confirm BL is correct</button>
                        <button
                          @click="cancelManualBLEntry"
                          class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                        >Cancel</button>
                      </div>

                      <div v-if="loadingManualBL" class="tw-mt-4">
                        <div class="tw-rounded-2xl tw-border tw-border-indigo-200/70 tw-bg-indigo-50/60 tw-p-4">
                          <p class="tw-text-sm tw-font-medium tw-text-indigo-900">Fetching Container details for BL {{billOfLadingNumber}}</p>
                          <div class="tw-mt-2 tw-text-sm tw-text-indigo-800">Loading...</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  v-if="!blManualOverride && showBlScrapeButton"
                  class="tw-mt-4 tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800"
                  :disabled="loadingBL || !billOfLadingNumber"
                  v-on:click="trackBillOfLading"
                >Scrape &amp; Add Container(s) for Bill of Lading</button>
              </div>
            </div>

            <div v-if="loadingBL" class="tw-mt-4">
              <div class="tw-rounded-2xl tw-border tw-border-indigo-200/70 tw-bg-indigo-50/60 tw-p-4">
                <p class="tw-text-sm tw-font-medium tw-text-indigo-900">
                  <template v-if="billOfLadingNumberScrapeSource === 'terminalWebsite'">
                    Fetching containers for BL {{ billOfLadingNumber }} at {{ blScrapeTerminal }}
                  </template>
                  <template v-else>
                    Fetching container details for BL {{ billOfLadingNumber }}
                  </template>
                </p>
                <div class="tw-mt-2 tw-text-sm tw-text-indigo-800">Loading...</div>
              </div>
            </div>
            <div v-if="loadingContainer" class="tw-mt-4">
              <div class="tw-rounded-2xl tw-border tw-border-indigo-200/70 tw-bg-indigo-50/60 tw-p-4">
                <p class="tw-text-sm tw-font-medium tw-text-indigo-900">Fetching Container details for container {{containerNumbers}} at Terminal {{terminal}}</p>
                <div class="tw-mt-2 tw-text-sm tw-text-indigo-800">Loading...</div>
              </div>
            </div>
          </div>
      </template>
    </Modal>

    <div v-if="!hideSearch" class="tw-h-2"></div>
    <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2" v-if="!isSearching">
      <Button @click="addNewContainerModal" variant="secondary">
        Scrape &amp; Add New Container(s)
      </Button>
      <Button
        :isDefault="true"
        :enableLoading="yardStatusLoading"
        v-on:click="updateYardStatus"
        title="Update the status of all containers at the terminals"
      >
        Re-scrape All Containers
      </Button>
    </div>
    <div v-if="!hideSearch">
      <SearchInput
        v-model="searchTerm"
        placeholder="Container Number, B/L Number, or Customer Name"
        class="tw-w-full sm:tw-max-w-lg"
      ></SearchInput>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import Modal from '@/components/core/Modal/Modal.vue';
import AddBLFromShiplineQuery from '@/gql/AddBLFromShipline.gql';
import AddBLFromTerminalQuery from '@/gql/AddBLFromTerminal.gql';
import AddContainerQuery from '@/gql/AddContainer.gql';
import { ACTIVE_BLS_REFETCH } from '@/utils/active_BLs_apollo';
import UpdateTerminalStatus from '@/gql/UpdateTerminalStatus.gql';
import SearchInput from '../core/SearchInput.vue';
import Button from '../core/Button.vue';

import AddBLManuallyMutation from '../../gql/containers/AddBLContainersManualOverride.gql';

import isContainer from '../../utils/container_validator';
import { billOfLadingMixin } from '../../utils/bill_of_lading';


export function toUppercase(value: any): string {
  if (!value) return '';
  return String(value).toUpperCase();
}

export default defineComponent({
  name: 'EnterContainer',
  components: {
    SearchInput,
    Button,
    Modal,
  },
  mixins: [billOfLadingMixin],
  props: {
    hideSearch: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isAddContainerModalOpen: false,
    activeTab: 'container' as 'container' | 'bl',
    hasAttemptedContainerSubmit: false,
    hasAttemptedManualSubmit: false,
    containerNumbers: '',
    billOfLadingNumber: '',
    billOfLadingNumberScrapeSource: 'terminalWebsite',
    blFormError: null,
    containerFormError: null,
    textDanger: false,
    loadingBL: false,
    loadingContainer: false,
    terminal: 'OICT',
    blScrapeTerminal: 'OICT',
    blManualOverride: false,
    askManualOverride: false,
    loadingManualBL: false,
    manualContainers: '',
    manualContainersTerminal: undefined,
    yardStatusLoading: false,
    searchTerm: '',
    fetchedBL: undefined,
    fetchedContainers: undefined,
  }),
  

  computed: {
    toUppercase,
    isSearching(): boolean {
      return (this as any).searchTerm.length > 0;
    },
    manualContainersList(): any[] {
      return (this as any).manualContainers
        .split('\n')
        .filter((x) => x !== '' && x !== ' ');
    },
    containersList(): any[] {
      return (this as any).containerNumbers
        .split('\n')
        .filter((x) => x !== '' && x !== ' ');
    },

    isContainerListValid(): boolean {
      const vm = (this as any);

      if (vm.containersList.length > 0) {
        return vm.containersList.reduce((acc, x) => {
          if (!acc) return false;

          if (!isContainer(x)) return false;

          return true;
        }, true);
      } else {
        return true;
      }
    },
    canSubmitContainers(): boolean {
      const vm = this as any;
      return vm.containersList.length > 0 && vm.isContainerListValid;
    },
    isManualContainerListValid(): boolean {
      const vm = (this as any);

      if (vm.manualContainersList.length > 0) {
        return vm.manualContainersList.reduce((acc, x) => {
          if (!acc) return false;

          if (!isContainer(x)) return false;

          return true;
        }, true);
      } else {
        return true;
      }
    },
    canSubmitManualContainers(): boolean {
      const vm = this as any;
      return vm.manualContainersList.length > 0 && vm.isManualContainerListValid;
    },
    showBlScrapeButton(): boolean {
      const vm = this as any;
      if (vm.billOfLadingNumberScrapeSource === 'shiplineWebsite') {
        return true;
      }
      return (
        vm.billOfLadingNumberScrapeSource === 'terminalWebsite'
      );
    },
  },
  watch: {
    containerNumbers(val: any) {
      const vm = (this as any);

      const uppercased = toUppercase(val);
      if (uppercased !== val) {
        vm.containerNumbers = uppercased;
      }
    },
    billOfLadingNumber(val: any) {
      const vm = (this as any);

      const trimmed = typeof val === 'string'
        ? val.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim()
        : val;
      const uppercased = toUppercase(trimmed);
      if (uppercased !== val) {
        vm.billOfLadingNumber = uppercased;
      }
    },
    searchTerm(val: any) {
      (this as any).$emit('searchTermChanged', val);
    },
    isAddContainerModalOpen(val: any) {
      if (!val) {
        this.resetModalState();
      }
    },
  },
  methods: {
    addNewContainerModal() {
      const vm = this as any;
      vm.activeTab = 'container';
      vm.isAddContainerModalOpen = true;
      vm.hasAttemptedContainerSubmit = false;
      vm.hasAttemptedManualSubmit = false;
    },
    resetContainers() {
      const vm = (this as any);

      vm.fetchedContainers = undefined;
      vm.fetchedBL = undefined;
    },
    addBLWithContainersManually() {
      const vm = (this as any);

      vm.hasAttemptedManualSubmit = true;

      if (!vm.manualContainersList || vm.manualContainersList.length === 0) {
        return;
      }

      if (!vm.isManualContainerListValid) {
        return;
      }

      console.log('Adding BL manually');
      vm.loadingManualBL = true;
      vm.$apollo
        .mutate({
          mutation: AddBLManuallyMutation,
          variables: {
            billOfLadingNumber: vm.billOfLadingNumber,
            containerIds: vm.manualContainersList,
            terminal: vm.manualContainersTerminal,
          },
        })
        .then((data) => {
          vm.billOfLadingNumber = '';
          vm.loadingManualBL = false;

          vm.blFormError = null;
          vm.blManualOverride = false;
          vm.manualContainers = '';
          vm.fetchedContainers =
            data.data.addManualBl.billOfLading.containers;
          vm.fetchedBL = data.data.addManualBl.billOfLading;
          vm.$emit('containerListChanged');
        })
        .catch((error) => {
          console.log('Full Apollo error:', JSON.stringify(error, null, 2));
          //vm.blFormError = errorMsg.message;
          vm.loadingManualBL = false;
        });
    },
    selectContainerTerminal(terminal: any) {
      (this as any).terminal = terminal;
    },
    triggerManualBLEntry() {
      (this as any).blManualOverride = true;
    },
    cancelManualBLEntry() {
      (this as any).blManualOverride = false;
    },
    trackContainer() {
      const vm = (this as any);

      vm.hasAttemptedContainerSubmit = true;

      if (!vm.containersList || vm.containersList.length === 0) {
        vm.containerFormError = 'Please enter at least one container.';
        return;
      }

      if (!vm.isContainerListValid) {
        vm.containerFormError = 'Your container Numbers are not valid. Please verify.';
        return;
      }

      vm.loadingContainer = true;
      vm.$apollo
        .mutate({
          mutation: AddContainerQuery,
          variables: {
            containerIds: vm.containersList,
            terminal: vm.terminal,
          },
        })
        // eslint-disable-next-line
        .then((data) => {
          vm.containerFormError = null;
          vm.textDanger = false;
          vm.loadingContainer = false;
          vm.containerNumbers = '';
          vm.$emit('containerListChanged');

          vm.fetchedContainers = data.data.createContainers.activeContainers;
        })
        .catch((error) => {
          const errorMsg = error.networkError.result.errors[0];
          vm.loadingContainer = false;
          vm.containerFormError = errorMsg.message;

          console.error(`Could not add Container with error ${error}`);
        });
    },
    trackBillOfLading() {
      const vm = (this as any);

      const onErrorHandler_ = (err) => {
        console.error(err);
        vm.loadingBL = false;
        vm.textDanger = true;
        vm.askManualOverride = true;

        if (err.graphQLErrors && err.graphQLErrors.length > 0) {
          vm.blFormError = err.graphQLErrors[0].message;
        } else if (err.networkError && err.networkError.result && err.networkError.result.errors) {
          vm.blFormError = err.networkError.result.errors[0].message;
        } else {
          vm.blFormError = err.message || "An unexpected error occurred";
        }
      }
      if (vm.billOfLadingNumber !== '') {
        vm.loadingBL = true;
        if (vm.billOfLadingNumberScrapeSource === 'shiplineWebsite') {
          vm.$apollo
          .mutate({
            mutation: AddBLFromShiplineQuery,
            variables: {
              billOfLadingNumber: vm.billOfLadingNumber,
            },
            refetchQueries: [ACTIVE_BLS_REFETCH],
          })
          // eslint-disable-next-line
          .then((data) => {
            vm.blFormError = null;
            vm.textDanger = false;
            vm.loadingBL = false;
            vm.billOfLadingNumber = '';
            console.log(`Created BL ${JSON.stringify(data)}`);

            vm.fetchedContainers = data.data.createBlFromShipline.billOfLading.containers;
            vm.fetchedBL = data.data.createBlFromShipline.billOfLading;

            vm.$emit('containerListChanged');
          })
          .catch(onErrorHandler_)
        } else {
          vm.$apollo
            .mutate({
              mutation: AddBLFromTerminalQuery,
              variables: {
                billOfLadingNumber: vm.billOfLadingNumber,
                terminal: vm.blScrapeTerminal,
              },
              refetchQueries: [ACTIVE_BLS_REFETCH],
            })
            // eslint-disable-next-line
            .then((data) => {
              vm.blFormError = null;
              vm.textDanger = false;
              vm.loadingBL = false;
              vm.billOfLadingNumber = '';
              console.log(`Created BL ${JSON.stringify(data)}`);

              vm.fetchedContainers = data.data.createBlFromTerminal.activeContainers;
              vm.fetchedBL = data.data.createBlFromTerminal.billOfLading;

              vm.$emit('containerListChanged');
            })
            .catch(onErrorHandler_)
        }
      } else {
        vm.blFormError = 'Please enter a Bill of Lading number.';
        vm.textDanger = true;
        vm.askManualOverride = false;
        console.log('Bill of Lading missing');
      }
    },
    updateYardStatus() {
      const vm = (this as any);

      vm.yardStatusLoading = true;
      vm.$apollo
        .mutate({
          mutation: UpdateTerminalStatus,
        })
        // eslint-disable-next-line
        .then((data) => {
          vm.yardStatusLoading = false;
          vm.$emit('containerListChanged');
        })
        .catch((error) => {
          // Error
          console.error(error);
          const errorMsg = error.networkError.result.errors[0];
          vm.blFormError = errorMsg.message;
          vm.textDanger = true;
          vm.yardStatusLoading = false;
        });
    },
    resetModalState() {
      const vm = this as any;
      vm.billOfLadingNumber = '';
      vm.containerNumbers = '';
      vm.blFormError = null;
      vm.containerFormError = null;
      vm.textDanger = false;
      vm.askManualOverride = false;
      vm.blManualOverride = false;
      vm.fetchedBL = undefined;
      vm.fetchedContainers = undefined;
    },
  },
});
</script>

<style>
</style>